// dashboard-v2 — Sidebar navigation, hand-rolled mirror of
// specs/seeds/dashboard-v2/navigation.yaml.
//
// Parity enforced by __tests__/nav-spec-parity.test.ts so this literal cannot
// drift from the YAML undetected.
//
// Hand-rolled exception per feedback_deterministic_codegen_over_hand_rolling.md
// (the v2 FE generator does not emit shell nav yet; this slice lands the
// surface, generator wiring is a follow-up).
//
// Group set + ordering + gradient_token are LOCKED per tier-1 spec
// specs/tier-01-architecture/dashboard-v2.app-navigation.admin-dashboard-v2.yaml.

export type GroupId =
  | "home"
  | "people"
  | "giving"
  | "events"
  | "channels"
  | "imports"
  | "automation"
  | "communications"
  | "settings"
  | "mygiving"
  // CHG-1659 — Reports + Analytics group (admin; visibleWhen
  // account.has_staff_role == true).
  | "reports"
  // CHG-1523 / ITER-3642 — platform-admin group (visibleWhen
  // account.is_platform_admin == true).
  | "platform";

export type GradientToken =
  | "g-home"
  | "g-people"
  | "g-giving"
  | "g-events"
  | "g-channels"
  | "g-imports"
  | "g-automation"
  | "g-communications"
  | "g-settings"
  | "g-mygiving"
  // CHG-1523 — slate→cyan; token-pair in tokens.css §3.5.
  | "g-platform";

export interface NavItem {
  id: string;
  label: string;
  route: string;
  icon?: string;
  forwardPromised?: boolean;
  permission?: string;
}

export interface NavGroup {
  id: GroupId;
  title: string;
  icon: string;
  gradientToken: GradientToken;
  items: NavItem[];
  // Declarative visibility gate. CHG-1473 / ITER-3593 — now ENFORCED
  // by Sidebar via evalVisibleWhen() against useAuth().account. Tiny DSL:
  //   `account.<field> == true|false`
  //   `account.<field>.length > N`
  //   joinable with ` && ` / ` || `
  // 9 admin groups gate on `account.has_staff_role == true`; "My Giving"
  // gates on `account.has_donor_profile == true`. Unknown / null account
  // → group hidden (fail-closed). See evaluator in shell/Sidebar.tsx.
  visibleWhen?: string;
}

// spec: specs/seeds/dashboard-v2/navigation.yaml
export const navGroups: NavGroup[] = [
  {
    id: "home",
    title: "Home",
    icon: "home",
    gradientToken: "g-home",
    visibleWhen: "account.has_staff_role == true",
    items: [
      {
        // CHG-1540 / ITER-3658 — IA correction. The org admin Overview
        // moves from "/" to "/overview" so "/" can serve as a
        // role-aware redirect target (RoleAwareRedirect routes each
        // role to its real home: /platform for platform-admin,
        // /overview for org-admin, /me for donor). nav.ts mirrors
        // the new canonical route.
        id: "overview",
        label: "Overview",
        route: "/overview",
        icon: "layout-dashboard",
      },
    ],
  },
  {
    id: "people",
    title: "People",
    icon: "users",
    gradientToken: "g-people",
    visibleWhen: "account.has_staff_role == true",
    items: [
      {
        // CHG-1473 — Donors page is fully mounted (CHG-1225); the
        // stale forwardPromised flag is removed so the row stays
        // clickable now that forwardPromised semantics are
        // "non-clickable + SOON badge."
        id: "donors",
        label: "Donors",
        route: "/donors",
        icon: "user",
      },
      {
        // CHG-1473 — route was /people/team (no App.tsx match → 404);
        // aligned to /team where TeamMembers actually mounts.
        id: "team",
        label: "Team & access",
        route: "/team",
        icon: "user-cog",
      },
      {
        // CHG-1663 / ITER-3772 (Wave 1 / Tier 1) — Roles v2 port.
        // Generator-emitted scaffolds + page wrappers. Sits under
        // People alongside donors + team rather than Settings
        // because roles are the *people-access* primitive (who can
        // do what); the rest of Settings is platform configuration.
        id: "roles",
        label: "Roles & permissions",
        route: "/roles",
        icon: "shield-check",
      },
    ],
  },
  {
    id: "giving",
    title: "Giving",
    icon: "heart",
    gradientToken: "g-giving",
    visibleWhen: "account.has_staff_role == true",
    items: [
      // CHG-1473 — all four entities below are fully mounted in App.tsx;
      // stale forwardPromised flags removed (clickable + no SOON badge).
      {
        id: "donations",
        label: "Donations",
        route: "/donations",
        icon: "hand-coins",
      },
      {
        id: "pledges",
        label: "Pledges",
        route: "/pledges",
        icon: "handshake",
      },
      {
        id: "recurring",
        label: "Recurring giving",
        route: "/recurring",
        icon: "repeat",
      },
      {
        id: "designations",
        label: "Designations",
        route: "/designations",
        icon: "folder-tree",
      },
      {
        // CHG-1661 / ITER-3770 — Campaigns relocated from Events to
        // Giving. A campaign is a fundraising drive (goal_minor_units,
        // raised_minor_units, donation rollup) — its data model is part of
        // the giving stack, not the live-events/sessions stack.
        // Placement here puts it alongside donations/pledges/recurring
        // /designations, which match operator expectation per
        // v2-platform-admin field-test. Moved with no route change
        // (/campaigns) so deep links + sidebar bookmarks survive.
        id: "campaigns",
        label: "Campaigns",
        route: "/campaigns",
        icon: "megaphone",
      },
      // CHG-1671 — donate-pages nav item retired along with the v2
      // admin pages (W4.1 / ITER-3780).
    ],
  },
  {
    id: "events",
    title: "Events",
    icon: "calendar",
    gradientToken: "g-events",
    visibleWhen: "account.has_staff_role == true",
    items: [
      {
        id: "sessions",
        label: "Live sessions",
        route: "/sessions",
        icon: "radio",
        forwardPromised: true,
      },
      {
        id: "signage",
        label: "Signage",
        route: "/signage",
        icon: "monitor",
        forwardPromised: true,
      },
    ],
  },
  {
    id: "channels",
    title: "Channels",
    icon: "radio",
    gradientToken: "g-channels",
    visibleWhen: "account.has_staff_role == true",
    items: [
      {
        // CHG-2460 / ITER-905202 — Widget Studio (S3). Real mounted surface.
        id: "widgets",
        label: "Widgets",
        route: "/channels/widgets",
        icon: "layout",
      },
      {
        // CHG-2474 / ITER-905215 — Widget analytics dashboard (S8).
        id: "widget-analytics",
        label: "Widget analytics",
        route: "/channels/widgets/analytics",
        icon: "bar-chart",
      },
      {
        id: "online",
        label: "Online channels",
        route: "/channels/online",
        icon: "globe",
        forwardPromised: true,
      },
      {
        id: "in-venue",
        label: "In-venue devices",
        route: "/channels/devices",
        icon: "tablet",
        forwardPromised: true,
      },
    ],
  },
  {
    id: "imports",
    title: "Data flows",
    icon: "download",
    gradientToken: "g-imports",
    visibleWhen: "account.has_staff_role == true",
    items: [
      {
        // CHG-1473 — Imports page is mounted; stale flag removed.
        id: "imports",
        label: "Imports & reconciliation",
        route: "/imports",
        icon: "upload",
      },
    ],
  },
  {
    id: "automation",
    title: "Automation",
    icon: "zap",
    gradientToken: "g-automation",
    visibleWhen: "account.has_staff_role == true",
    items: [
      {
        id: "automation",
        label: "Automation studio",
        route: "/automation",
        icon: "workflow",
        forwardPromised: true,
      },
    ],
  },
  {
    id: "communications",
    title: "Communications",
    icon: "message-circle",
    gradientToken: "g-communications",
    visibleWhen: "account.has_staff_role == true",
    items: [
      {
        // CHG-1473 — route was /messaging (no App.tsx match → 404);
        // aligned to /messages where Messages actually mounts.
        id: "messaging",
        label: "Messaging workspace",
        route: "/messages",
        icon: "message-square",
      },
      {
        // CHG-1473 — route was /messaging/templates (no App.tsx match
        // → 404); aligned to /templates where Templates mounts.
        id: "templates",
        label: "Templates",
        route: "/templates",
        icon: "file-text",
      },
    ],
  },
  {
    // CHG-1659 — Reports + Analytics admin group (port wave 1 / tier 1).
    // 4 admin pages under /reports/* gated by alora-giving.analytics.read.
    // Spec authority for each page lives in its entity-hint under
    // specs/tier-05-experience/ (analytics-dashboard, reports,
    // annual-reports, impact-reports). Documented hand-roll exception
    // per DRIFT-702 (v2gen.py has no dashboard_v2 / menu_v2 emit modes).
    id: "reports",
    title: "Reports",
    icon: "bar-chart",
    gradientToken: "g-imports",
    visibleWhen: "account.has_staff_role == true",
    items: [
      {
        id: "reports-landing",
        label: "Reports",
        route: "/reports",
        icon: "list",
      },
      {
        id: "reports-analytics",
        label: "Analytics dashboard",
        route: "/reports/analytics",
        icon: "activity",
      },
      {
        id: "reports-annual",
        label: "Annual reports",
        route: "/reports/annual",
        icon: "calendar",
      },
      {
        id: "reports-impact",
        label: "Impact reports",
        route: "/reports/impact",
        icon: "sparkles",
      },
    ],
  },
  {
    id: "settings",
    title: "Settings",
    icon: "settings",
    gradientToken: "g-settings",
    visibleWhen: "account.has_staff_role == true",
    items: [
      {
        // CHG-1473 — route was /settings/organization (no App.tsx
        // match → 404); aligned to /settings where Settings mounts.
        id: "org-settings",
        label: "Organization",
        route: "/settings",
        icon: "building",
      },
      {
        id: "billing",
        label: "Billing & wallets",
        route: "/settings/billing",
        icon: "credit-card",
        forwardPromised: true,
      },
      // CHG-1642 / ITER-3752 — Wave 1 Tier 1 Stripe Accounts admin
      // port. Lives under Settings since the v1 dashboard treats the
      // Stripe-account list as a settings concern (one row per
      // connected Stripe account, OAuth-connected at org level).
      {
        id: "stripe-accounts",
        label: "Stripe accounts",
        route: "/stripe-accounts",
        icon: "credit-card",
        permission: "alora-giving.stripe-account.read",
      },
      {
        id: "integrations",
        label: "Integrations",
        route: "/settings/integrations",
        icon: "plug",
        forwardPromised: true,
      },
      // CHG-1643 / ITER-3753 (Wave 1 Tier 1) — org-scoped log surfaces
      // ported from v1. Permission codes match the BE require_permission()
      // decorators on activity_feed.py / audit.py / delivery_logs.py.
      {
        id: "activity",
        label: "Activity feed",
        route: "/activity",
        icon: "activity",
        permission: "alora-giving.activity.read",
      },
      {
        id: "audit-logs",
        label: "Audit trail",
        route: "/audit-logs",
        icon: "shield",
        permission: "alora-giving.audit.read",
      },
      {
        id: "delivery-logs",
        label: "Delivery log",
        route: "/delivery-logs",
        icon: "mail",
        permission: "alora-giving.delivery-log.read",
      },
    ],
  },
  {
    // CHG-1502 / ITER-3619 (donor-portal γ.3) — My Giving cleanup pass.
    // Group reaches its canonical Group-β/γ shape: exactly 6 real
    // donor-portal items, no forwardPromised stub remaining, ordered
    // by donor-mental-model (history → recurring → pledges → receipts,
    // then identity → preferences). Every item routes to a shipping
    // page; none renders a SOON badge.
    //
    // Items in declared order (anchor CHG = the slice that landed
    // each page; auth surface for all: ProtectedRoute auth-only):
    //   my-giving      /me/giving       MyGiving       CHG-1495 β.1
    //   my-recurring   /me/recurring    MyRecurring    CHG-1496 β.2
    //   my-pledges     /me/pledges      MyPledges      CHG-1499 β.3
    //   my-receipts    /me/receipts     MyReceipts     β.4 (vocab in
    //                                                  flight; lands by
    //                                                  this CHG's deploy)
    //   my-profile     /me/profile      MyProfile      CHG-1494 β.5
    //   my-preferences /me/preferences  MyPreferences  CHG-1498 β.6
    id: "mygiving",
    title: "My Giving",
    icon: "gift",
    gradientToken: "g-mygiving",
    visibleWhen: "account.has_donor_profile == true",
    items: [
      {
        // CHG-1513 wave 1 — donor portal dashboard root. MyOverview.tsx
        // mounted at /my-giving (was /me before portal-pivot retarget).
        id: "my-giving-overview",
        label: "Overview",
        route: "/my-giving",
        icon: "home",
      },
      {
        // CHG-1495 β.1 — donor donations history.
        id: "my-giving",
        label: "My giving history",
        route: "/my-giving/donations",
        icon: "heart",
      },
      {
        // CHG-1496 β.2 — donor's active + cancelled recurring gifts.
        id: "my-recurring",
        label: "My recurring gifts",
        route: "/my-giving/recurring",
        icon: "repeat",
      },
      {
        // CHG-1499 β.3 — donor pledges + progress.
        id: "my-pledges",
        label: "My pledges",
        route: "/my-giving/pledges",
        icon: "target",
      },
      {
        // CHG-1503 / β.4 — MyReceipts.tsx mounted at /me/receipts; consumes
        // GET /api/v1/me/receipts (CHG-1481 α.5) for the paged catalog +
        // GET /api/v1/me/receipts/{id}/pdf for per-receipt PDF re-issue.
        // Nav entry placement preempted by iter/navclean-df76c178; CHG-1503
        // merge keeps that placement + retains the canonical CHG-1503
        // comment + my-receipts route (now live with the page mounted).
        id: "my-receipts",
        label: "My receipts",
        route: "/my-giving/receipts",
        icon: "receipt",
      },
      {
        // CHG-1517 wave 2 — MyImpact.tsx at /my-giving/impact; consumes
        // GET /api/v1/my-giving/impact (portal.py). Per-org + per-program
        // breakdown of donor giving with progress bars.
        id: "my-impact",
        label: "My impact",
        route: "/my-giving/impact",
        icon: "sparkles",
      },
      {
        // comp-ε GAP-3a — MyCalendar.tsx at /my-giving/calendar; consumes
        // donor_portal_calendar.py GET /my-giving/calendar/*. Cross-org agenda
        // of events / pledge due dates / recurring + an iCal subscribe modal.
        id: "my-giving-calendar",
        label: "My calendar",
        route: "/my-giving/calendar",
        icon: "calendar",
      },
      {
        // CHG-1521 wave 3 — MyCommunications.tsx at /my-giving/communications.
        // Donor inbox of receipts/reminders/updates from supported orgs.
        // Consumes portal.py GET /my-giving/communications.
        id: "my-communications",
        label: "My communications",
        route: "/my-giving/communications",
        icon: "mail",
      },
      {
        // CHG-1521 wave 3 — MyFundraisingPages.tsx at /my-giving/fundraising.
        // Peer-to-peer fundraising pages the donor has created with inline
        // pause/resume row actions. Consumes my_giving_peer_pages.py at
        // /my-giving/peer-pages/*. Create/edit deferred to a wave 4 CHG.
        id: "my-fundraising",
        label: "My fundraising",
        route: "/my-giving/fundraising",
        icon: "users",
      },
      {
        // CHG-1494 / ITER-3612 (donor-portal β.5) — first real donor-
        // portal page. Mounts MyProfile.tsx which consumes
        // GET+PATCH /api/v1/me/profile (CHG-1478 α.1).
        id: "my-profile",
        label: "Profile",
        route: "/my-giving/profile",
        icon: "user",
      },
      {
        // CHG-1498 β.6 — donor preferences self-edit
        // (locale + contact channel + intent topics).
        id: "my-preferences",
        label: "Preferences",
        route: "/my-giving/preferences",
        icon: "settings",
      },
    ],
  },
  {
    // CHG-1523 / ITER-3642 — Platform admin group. visibleWhen
    // account.is_platform_admin == true (derived in AuthContext from
    // memberships where role === 'platform-admin' or any active role
    // assignment with that slug). Mounted last so its presence doesn't
    // perturb the 10-group frequency-ordered IA for non-platform-admin
    // accounts (the group simply does not render for them — same gate
    // pattern as "mygiving" for donor accounts).
    //
    // 6 items. One of them (v2-adoption) already routes to a real page
    // (CHG-1307); the other five carry forwardPromised: true ("Soon"
    // badge) until their dedicated v2 pages land. Same donor-portal
    // precedent — surface the IA early so platform admins know where
    // each capability will live.
    id: "platform",
    title: "Platform",
    icon: "shield",
    gradientToken: "g-platform",
    visibleWhen: "account.is_platform_admin == true",
    items: [
      {
        id: "platform-organizations",
        label: "Organizations",
        route: "/platform/organizations",
        icon: "building",
        forwardPromised: true,
      },
      {
        id: "platform-members",
        label: "Members",
        route: "/platform/members",
        icon: "users",
        forwardPromised: true,
      },
      // CHG-1643 / ITER-3753 (Wave 1 Tier 1) — promoted from
      // forwardPromised stub to live page (PlatformActivityFeed /
      // PlatformAuditLogs in App.tsx). BE enforces platform-admin on
      // scope=platform.
      {
        id: "platform-activity",
        label: "Activity",
        route: "/platform/activity",
        icon: "activity",
      },
      {
        id: "platform-audit-logs",
        label: "Audit logs",
        route: "/platform/audit-logs",
        icon: "file-text",
      },
      {
        id: "platform-billing",
        label: "Billing",
        route: "/platform/billing",
        icon: "credit-card",
        forwardPromised: true,
      },
      {
        // CHG-1307 — V2AdoptionDashboard is mounted at /platform/v2-adoption
        // (App.tsx wraps it in ProtectedRoute with the
        // alora-giving.organization.create gate, which is the
        // platform-admin signal on the backend). No forwardPromised
        // because this row routes to a real page.
        id: "platform-v2-adoption",
        label: "v2 Adoption",
        route: "/platform/v2-adoption",
        icon: "bar-chart-3",
      },
    ],
  },
];

// Returns the group whose item route matches the current pathname most
// specifically. Returns null if no group claims the path.
//
// Match rules:
//   - exact match wins
//   - prefix match (route + "/") accepted; longer prefix wins
//   - root "/" only matches exact "/"
export function findActiveGroup(
  pathname: string,
): { group: NavGroup; item: NavItem } | null {
  let best: { group: NavGroup; item: NavItem; matchLen: number } | null = null;
  for (const group of navGroups) {
    for (const item of group.items) {
      const r = item.route;
      let matched = false;
      let matchLen = 0;
      if (r === "/") {
        if (pathname === "/") {
          matched = true;
          matchLen = 1;
        }
      } else if (pathname === r) {
        matched = true;
        matchLen = r.length + 1; // exact beats prefix of same string
      } else if (pathname.startsWith(r + "/")) {
        matched = true;
        matchLen = r.length;
      }
      if (matched && (!best || matchLen > best.matchLen)) {
        best = { group, item, matchLen };
      }
    }
  }
  if (!best) return null;
  return { group: best.group, item: best.item };
}
